import React, { useEffect, useMemo, useState } from 'react';

import { Checkbox, Icon, PrimaryButton, SecondaryButton } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import { ThemeProvider } from 'styled-components';

import List, { ListItem } from 'components/list';
import useDialogModal from 'hooks/use-dialog-modal';
import { IStaticPageRoute } from 'remote/queries/static-page';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ClickEventComponentNames } from 'state/mParticle/constants';
import { theme as componentLibraryTheme } from 'styles/configure-theme';

import ModalAgreementDetail from './modal-agreement-detail';
import {
  ContentCheckbox,
  ContentDescription,
  ContentHeader,
  ContentListLinks,
  ModalButtons,
  ModalContainer,
  ModalContent,
  ModalContentAgreements,
  ModalWrapper,
  Row,
  TitleModal,
} from './styled';

interface IModalAgreement {
  continueUpdate: () => Promise<any>;
  cancel: () => void;
  mParticleEventData: ImParticleEventData;
  requiredTerms: IStaticPageRoute[];
}

const ModalAgreement: React.FC<IModalAgreement> = ({
  continueUpdate,
  cancel,
  mParticleEventData: { modalAppearanceEventMessage, modalHeader, modalMessage },
  requiredTerms,
}) => {
  const enableModalUpdatedAgreements = useFlag(
    LaunchDarklyFlag.ENABLE_REQUIRED_ACCEPTANCE_AGREEMENT_INFO
  );
  const { formatMessage } = useIntl();
  const [isChecked, setcheck] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [showModalPolicies, setModalPolicies] = useState(false);
  const [newTerms, setNewTerms] = useState({} as IStaticPageRoute);
  const { logEvent, logRBIEvent } = useMParticleContext();

  const [Confirm, openConfirm] = useDialogModal<{ id: string }>({
    onConfirm: () => {
      cancel();
    },
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation: Sign Out',
  });

  const requiredTermsSortedAlphabetically = useMemo(
    () =>
      requiredTerms.sort((a, b) => {
        const aTitle = a.localeTitle?.locale || a.title || '';
        const bTitle = b.localeTitle?.locale || b.title || '';
        return aTitle.localeCompare(bTitle);
      }),
    [requiredTerms]
  );

  const onClickDisagree = (e: React.MouseEvent) => {
    e.preventDefault();
    openConfirm();

    logRBIEvent({
      name: CustomEventNames.CLICK_EVENT,
      type: EventTypes.Navigation,
      attributes: {
        component: ClickEventComponentNames.BUTTON,
        text: 'Disagree updated agreement and logout',
      },
    });
  };

  const handleChange = () => {
    return isChecked ? setcheck(false) : setcheck(true);
  };

  const onClickContinue = async () => {
    setLoadingModal(true);
    const result = await continueUpdate();

    if (result) {
      setLoadingModal(false);
    }

    logRBIEvent({
      name: CustomEventNames.CLICK_EVENT,
      type: EventTypes.Navigation,
      attributes: {
        component: ClickEventComponentNames.BUTTON,
        text: 'Accepted updated agreement and continue',
      },
    });
  };
  const openTermsModal = (terms: IStaticPageRoute) => {
    setNewTerms(terms);
    setModalPolicies(true);
  };

  const closeTermsModal = () => {
    setModalPolicies(false);
  };

  useEffect(() => {
    logEvent(CustomEventNames.MODAL_APPEARANCE, EventTypes.Other, {
      Message: modalAppearanceEventMessage,
      ModalHeader: modalHeader,
      ModalMessage: modalMessage,
    });
  }, [logEvent, modalAppearanceEventMessage, modalHeader, modalMessage]);

  return (
    <>
      {enableModalUpdatedAgreements && (
        <ThemeProvider theme={componentLibraryTheme}>
          <ModalContainer>
            <ModalContentAgreements>
              <ModalWrapper>
                <ModalContent>
                  <ContentHeader>
                    <TitleModal aria-label={formatMessage({ id: 'updatedAgreements' })}>
                      {formatMessage({ id: 'updatedAgreements' })}
                    </TitleModal>
                  </ContentHeader>
                  <ContentDescription
                    aria-label={formatMessage({ id: 'reviewAcceptUpdatedAgreement' })}
                  >
                    {formatMessage({ id: 'reviewAcceptUpdatedAgreement' })}
                  </ContentDescription>
                  <ContentCheckbox>
                    <Checkbox
                      aria-label={formatMessage({ id: 'updatedAgreementsText' })}
                      label={formatMessage({ id: 'updatedAgreementsText' })}
                      name="checkAgree"
                      checked={isChecked}
                      onChange={handleChange}
                    />
                  </ContentCheckbox>
                  <ContentListLinks>
                    <List>
                      {requiredTermsSortedAlphabetically.map((terms: IStaticPageRoute) => (
                        <ListItem key={terms._id} onClick={() => openTermsModal(terms)}>
                          <Row>
                            <span>{terms.localeTitle?.locale || terms.title}</span>
                            <Icon
                              icon="next"
                              aria-hidden
                              color="icon-default"
                              width="15px"
                              height="15px"
                            />
                          </Row>
                        </ListItem>
                      ))}
                    </List>
                  </ContentListLinks>
                  <ModalButtons>
                    <PrimaryButton
                      fullWidth
                      disabled={!isChecked}
                      aria-label={formatMessage({ id: 'continue' })}
                      onClick={onClickContinue}
                      loading={loadingModal}
                    >
                      {formatMessage({ id: 'continue' })}
                    </PrimaryButton>
                    <SecondaryButton
                      fullWidth
                      aria-label={formatMessage({ id: 'disagreeLogout' })}
                      onClick={onClickDisagree}
                    >
                      {formatMessage({ id: 'disagreeLogout' })}
                    </SecondaryButton>
                  </ModalButtons>
                  <Confirm
                    heading={formatMessage({ id: 'signOut' })}
                    body={formatMessage({ id: 'signOutConfirmSubheader' })}
                    confirmLabel={formatMessage({ id: 'yes' })}
                    cancelLabel={formatMessage({ id: 'no' })}
                  />
                </ModalContent>

                {showModalPolicies && (
                  <ModalAgreementDetail onBack={closeTermsModal} termsObject={newTerms} />
                )}
              </ModalWrapper>
            </ModalContentAgreements>
          </ModalContainer>
        </ThemeProvider>
      )}
    </>
  );
};

export default ModalAgreement;
